import { COUNTRY_TIMEZONES, COUNTRY_TIMEZONE_OVERRIDES, TIMEZONE_SET } from '@/constants/timezones'

/**
 * Pure derivation of a venue's time zone (issue #239).
 *
 * The country data comes from the pinned constants in constants/timezones.ts (generated from
 * PHP and checked by tests/Unit/Support/TimezonesTest.php). This module only applies the
 * ordering of the sources.
 */

export type VenueTimezoneSource = 'venue_country' | 'browser'

export interface VenueTimezoneResult {
  timezone: string | null
  source: VenueTimezoneSource | null
}

interface DerivationInput {
  country: string | null
  browserTimezone: string | null
}

/** Time zone of this browser, or null when it cannot be resolved. */
export function browserTimezone(): string | null {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
    return zone ? zone : null
  } catch {
    return null
  }
}

/** True when the zone is part of the backend list. */
export function isSupportedTimezone(timezone: string): boolean {
  return TIMEZONE_SET.has(timezone)
}

function normaliseCountry(country: string | null): string | null {
  if (country === null) return null
  const code = country.trim().toUpperCase()
  return /^[A-Z]{2}$/.test(code) ? code : null
}

/** All zones PHP lists for the country; [] for null, malformed or unknown codes. */
export function zonesForCountry(country: string | null): ReadonlyArray<string> {
  const code = normaliseCountry(country)
  if (code === null) return []
  return COUNTRY_TIMEZONES[code] ?? []
}

/** The override when it is still listed for the country, else the single zone, else null. */
export function suggestedTimezoneForCountry(country: string | null): string | null {
  const code = normaliseCountry(country)
  if (code === null) return null
  const zones = zonesForCountry(code)
  const override = COUNTRY_TIMEZONE_OVERRIDES[code]
  if (override !== undefined && zones.includes(override)) return override
  return zones.length === 1 ? (zones[0] ?? null) : null
}

/** Rule 1 (country zone) or rule 3 (browser zone confirmed by a multi-zone country). */
function deriveFromCountry(input: DerivationInput): VenueTimezoneResult {
  const suggested = suggestedTimezoneForCountry(input.country)
  if (suggested !== null) {
    return { timezone: suggested, source: 'venue_country' }
  }
  const zones = zonesForCountry(input.country)
  if (zones.length > 1 && input.browserTimezone !== null && zones.includes(input.browserTimezone)) {
    return { timezone: input.browserTimezone, source: 'browser' }
  }
  return { timezone: null, source: null }
}

/**
 * Create-mode derivation. The first match wins:
 * 1. the single zone of the country (Germany: Europe/Berlin);
 * 2. no country: the browser zone, if it is in the backend list;
 * 3. a multi-zone country that contains the browser zone: the browser zone;
 * otherwise no value, so the organiser has to pick manually.
 */
export function deriveCreateTimezone(input: DerivationInput): VenueTimezoneResult {
  const fromCountry = deriveFromCountry(input)
  if (fromCountry.timezone !== null) {
    return fromCountry
  }

  const hasCountry = input.country !== null && input.country.trim() !== ''
  if (!hasCountry && input.browserTimezone !== null && isSupportedTimezone(input.browserTimezone)) {
    return { timezone: input.browserTimezone, source: 'browser' }
  }

  return { timezone: null, source: null }
}

/**
 * Edit-mode derivation, used only after the organiser changes the country: rule 1 or
 * rule 3, never rule 2 (an unconfirmed device zone is weaker than the stored value).
 */
export function deriveEditTimezone(input: DerivationInput): VenueTimezoneResult {
  return deriveFromCountry(input)
}
